import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Divider, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import { BID_ANALYSIS_CHART_HEIGHT } from 'components/reports/Metrics/constants'
import { BidAnalysisHistogramDto } from 'generated/graphql'
import { formatDurationMinutes } from 'helpers/duration.helpers'

import { BucketBarChart } from '../components/BucketBarChart'
import { StatPair } from '../components/StatPair'

export type TimeHistogram = Pick<
  BidAnalysisHistogramDto,
  'totalJobs' | 'medianMinutes' | 'averageMinutes' | 'buckets'
>

export interface TimeSeries {
  label: string
  color: string
  histogram?: TimeHistogram | null
}

interface Props {
  title: string
  info: string
  footnote: string
  series: TimeSeries[]
  averageCaption?: string
  loading?: boolean
}

export const TimeToHistogramCard = ({
  title,
  info,
  footnote,
  series,
  averageCaption = 'Average',
  loading,
}: Props) => {
  const labels =
    series.find((s) => s.histogram?.buckets?.length)?.histogram?.buckets.map((b) => b.label) ?? []

  const chartSeries = series.map((s) => ({
    label: s.label,
    color: s.color,
    values: labels.map((label) => {
      const bucket = s.histogram?.buckets?.find((b) => b.label === label)
      return bucket?.jobCount ?? 0
    }),
  }))

  const isMultiSeries = series.length > 1

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 2,
        borderTop: series[0]?.color ? `3px solid ${series[0].color}` : undefined,
      }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        mb={2}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='subtitle1' fontWeight={700} sx={{ color: 'primary.main' }}>
            {title}
          </Typography>
          <Tooltip title={info}>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Stack>

        <Stack
          direction='row'
          divider={<Divider orientation='vertical' flexItem />}
          sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
          <StatPair
            caption='Median'
            loading={loading}
            values={series.map((s) => ({
              value: formatDurationMinutes(s.histogram?.medianMinutes),
              color: isMultiSeries ? s.color : undefined,
            }))}
          />
          <StatPair
            caption={averageCaption}
            loading={loading}
            values={series.map((s) => ({
              value: formatDurationMinutes(s.histogram?.averageMinutes),
              color: isMultiSeries ? s.color : undefined,
            }))}
          />
        </Stack>
      </Stack>

      {loading ? (
        <Skeleton
          variant='rectangular'
          height={BID_ANALYSIS_CHART_HEIGHT}
          sx={{ borderRadius: 1 }}
        />
      ) : (
        <BucketBarChart labels={labels} series={chartSeries} xAxisLabel='Time' />
      )}

      <Stack direction='row' spacing={1} alignItems='flex-start' mt={1}>
        <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
        <Typography variant='caption' color='text.secondary'>
          {footnote}
        </Typography>
      </Stack>
    </Paper>
  )
}
